/**
 * Forge suite TTS catalog — mirrors BookForge forge-backend tts-catalog.
 * Live: GET /api/tts/status + GET /api/models/catalog?modality=tts
 */

import { loadSuiteSettings, SUITE_RELAY_URL } from "../suite/settings";

export type TtsTier = "studio" | "budget" | "pro";

export type TtsVoiceOpt = { id: string; label: string; hint?: string };

export type TtsModelOpt = {
  id: string;
  label: string;
  tier: TtsTier;
  hint?: string;
  description?: string;
  voices?: TtsVoiceOpt[];
  costPer1kCharsUsd?: number;
  /** false = catalog placeholder only (relay returns 501) */
  routable?: boolean;
};

/**
 * Cloudflare lists these, but Unified Billing 402s them (code 2021: BYOK only).
 * Do not show them in VoiceForge catalogs.
 */
export function isByokOnlyCloudflareModel(id: string): boolean {
  const lower = (id || "").toLowerCase();
  return (
    lower.includes("eleven-flash") ||
    lower === "elevenlabs/eleven-flash-v2-5" ||
    lower.endsWith("/eleven-flash-v2-5")
  );
}

/**
 * Suite synthesize supports these providers today.
 * Inworld / MiniMax are listed in the Forge catalog but not routed yet (501).
 * Music models are not speech. BYOK-only CF models stay hidden.
 */
export function isSuiteSynthesizableModel(id: string): boolean {
  const lower = (id || "").toLowerCase();
  if (!lower) return false;
  if (isByokOnlyCloudflareModel(lower)) return false;
  if (lower.startsWith("inworld/") || lower.startsWith("minimax/")) return false;
  if (lower.includes("inworld") || lower.includes("minimax")) return false;
  if (lower.includes("music") && !/tts|speech|aura|kokoro|bark|grok/.test(lower)) {
    return false;
  }
  return true;
}

export type SuiteTtsApply =
  | { ok: true; modelId: string; note?: string }
  | { ok: false; error: string };

/**
 * Map a catalog model id onto a synthesizable suite TTS id.
 * Row click and "Use this TTS engine" both go through here.
 */
export function applyInferenceTtsModel(id: string): SuiteTtsApply {
  const raw = (id || "").trim();
  if (!raw) return { ok: false, error: "No model selected" };
  const lower = raw.toLowerCase();

  if (isByokOnlyCloudflareModel(lower)) {
    return {
      ok: false,
      error: `${raw} is not on Cloudflare Unified Billing. Pick Multilingual v2, Turbo v2.5, or v3.`,
    };
  }
  if (lower.includes("music") && !/tts|speech|aura|kokoro|bark|grok/.test(lower)) {
    return {
      ok: false,
      error: `${raw} is a music model, not speech. Pick a TTS engine.`,
    };
  }
  if (lower.startsWith("inworld/") || lower.startsWith("minimax/") || lower.includes("inworld") || lower.includes("minimax")) {
    return {
      ok: false,
      error: `${raw} is listed but not routed yet. Use Grok, OpenAI, Kokoro, Aura, or ElevenLabs.`,
    };
  }

  // Conversational / alias ids → engines /api/tts/synthesize actually runs
  if (lower === "xai/grok-voice" || lower === "grok-voice") {
    return { ok: true, modelId: "xai/grok-tts", note: "Grok Voice → Grok TTS" };
  }
  if (lower === "elevenlabs/eleven-v3" || lower.endsWith("/eleven-v3")) {
    return { ok: true, modelId: "elevenlabs/eleven-v3" };
  }
  if (lower.includes("gemini") && lower.includes("tts")) {
    return { ok: true, modelId: raw };
  }
  if (lower.includes("breeze")) {
    return { ok: true, modelId: "forge/suite-breeze-tts" };
  }
  if (lower.includes("kokoro")) {
    return { ok: true, modelId: "forge/suite-kokoro" };
  }

  return { ok: true, modelId: raw };
}

/** Prefer studio defaults when stored selection is a placeholder. */
export function pickDefaultSuiteModel(models: TtsModelOpt[]): string {
  const live = models.filter(
    (m) => m.routable !== false && isSuiteSynthesizableModel(m.id),
  );
  if (live.some((m) => m.id === DEFAULT_SUITE_MODEL_ID)) return DEFAULT_SUITE_MODEL_ID;
  if (live[0]) return live[0].id;
  return DEFAULT_SUITE_MODEL_ID;
}

export const TTS_TIER_LABELS: Record<TtsTier, string> = {
  studio: "Studio (recommended)",
  budget: "Budget",
  pro: "Pro (pass-through)",
};

/** Static fallback when relay catalog is offline (same IDs as BookForge). */
export const FALLBACK_TTS_MODELS: TtsModelOpt[] = [
  {
    id: "xai/grok-tts",
    label: "xAI Grok TTS",
    tier: "studio",
    hint: "recommended",
    description: "Expressive multi-voice · speech tags",
    voices: [
      { id: "ara", label: "Ara (warm, friendly)" },
      { id: "eve", label: "Eve (energetic, default)" },
      { id: "leo", label: "Leo (authoritative)" },
      { id: "rex", label: "Rex (clear · narrator)" },
      { id: "sal", label: "Sal (smooth, balanced)" },
    ],
    costPer1kCharsUsd: 0.015,
  },
  {
    id: "openai/tts-1",
    label: "OpenAI TTS-1",
    tier: "studio",
    hint: "~$0.015/1k chars",
    voices: [
      { id: "alloy", label: "Alloy" },
      { id: "echo", label: "Echo" },
      { id: "fable", label: "Fable" },
      { id: "onyx", label: "Onyx" },
      { id: "nova", label: "Nova" },
      { id: "shimmer", label: "Shimmer" },
    ],
    costPer1kCharsUsd: 0.015,
  },
  {
    id: "openai/tts-1-hd",
    label: "OpenAI TTS-1 HD",
    tier: "studio",
    hint: "~$0.030/1k chars",
    voices: [
      { id: "alloy", label: "Alloy" },
      { id: "echo", label: "Echo" },
      { id: "fable", label: "Fable" },
      { id: "onyx", label: "Onyx" },
      { id: "nova", label: "Nova" },
      { id: "shimmer", label: "Shimmer" },
    ],
    costPer1kCharsUsd: 0.03,
  },
  {
    id: "forge/suite-kokoro",
    label: "vividlex-kokoro",
    tier: "budget",
    hint: "kokoro-fastapi",
    description: "HF Inference Endpoint copperscout1/vividlex-kokoro",
    voices: [
      { id: "af_heart", label: "Heart (F)" },
      { id: "af_bella", label: "Bella (F)" },
      { id: "af_sarah", label: "Sarah (F)" },
      { id: "af_nicole", label: "Nicole (F)" },
      { id: "am_adam", label: "Adam (M)" },
      { id: "am_michael", label: "Michael (M · narrator)" },
      { id: "am_fenrir", label: "Fenrir (M)" },
      { id: "bf_emma", label: "Emma (F UK)" },
      { id: "bm_george", label: "George (M UK)" },
    ],
    costPer1kCharsUsd: 0.002,
  },
  {
    id: "hexgrad/Kokoro-82M",
    label: "Kokoro 82M",
    tier: "budget",
    hint: "fal fallback",
    voices: [
      { id: "af_heart", label: "Heart (F)" },
      { id: "af_bella", label: "Bella (F)" },
      { id: "af_sarah", label: "Sarah (F)" },
      { id: "af_nicole", label: "Nicole (F)" },
      { id: "am_adam", label: "Adam (M)" },
      { id: "am_michael", label: "Michael (M · narrator)" },
      { id: "am_fenrir", label: "Fenrir (M)" },
      { id: "bf_emma", label: "Emma (F UK)" },
      { id: "bm_george", label: "George (M UK)" },
    ],
    costPer1kCharsUsd: 0.002,
  },
  {
    id: "fal-ai/kokoro",
    label: "Kokoro (fal)",
    tier: "budget",
    hint: "fal router",
    voices: [
      { id: "af_heart", label: "Heart (F)" },
      { id: "af_bella", label: "Bella (F)" },
      { id: "am_adam", label: "Adam (M)" },
      { id: "am_michael", label: "Michael (M)" },
    ],
    costPer1kCharsUsd: 0.002,
  },
  {
    id: "fal-ai/chatterbox",
    label: "Chatterbox",
    tier: "budget",
    hint: "expressive",
    voices: [
      { id: "default", label: "Default" },
      { id: "expressive", label: "Expressive" },
      { id: "calm", label: "Calm" },
    ],
    costPer1kCharsUsd: 0.003,
  },
  {
    id: "@cf/deepgram/aura-1",
    label: "CF Aura-1",
    tier: "budget",
    hint: "Workers AI",
    voices: [
      { id: "asteria", label: "Asteria" },
      { id: "luna", label: "Luna" },
      { id: "orion", label: "Orion" },
      { id: "zeus", label: "Zeus" },
      { id: "hera", label: "Hera" },
    ],
    costPer1kCharsUsd: 0.015,
  },
  {
    id: "@cf/deepgram/aura-2-en",
    label: "CF Aura-2 English",
    tier: "budget",
    hint: "Workers AI",
    voices: [
      { id: "asteria", label: "Asteria" },
      { id: "luna", label: "Luna" },
      { id: "orion", label: "Orion" },
      { id: "zeus", label: "Zeus" },
    ],
    costPer1kCharsUsd: 0.03,
  },
  {
    id: "suno/bark-small",
    label: "Bark small",
    tier: "budget",
    hint: "multi-speaker",
    voices: [
      { id: "v2/en_speaker_0", label: "Speaker 0" },
      { id: "v2/en_speaker_1", label: "Speaker 1" },
      { id: "v2/en_speaker_6", label: "Speaker 6" },
      { id: "v2/en_speaker_9", label: "Speaker 9" },
    ],
  },
  {
    id: "facebook/mms-tts-eng",
    label: "MMS English",
    tier: "budget",
    hint: "single speaker",
    voices: [{ id: "default", label: "Default" }],
  },
  {
    id: "elevenlabs/eleven-multilingual-v2",
    label: "ElevenLabs Multilingual v2",
    tier: "pro",
    hint: "pass-through",
    voices: [
      { id: "21m00Tcm4TlvDq8ikWAM", label: "Rachel (default)" },
      { id: "pNInz6obpgDQGcFmaJgB", label: "Adam" },
      { id: "EXAVITQu4vr4xnSDxMaL", label: "Bella" },
      { id: "ErXwobaYiN019PkySvjV", label: "Antoni" },
    ],
  },
  {
    id: "elevenlabs/eleven-turbo-v2-5",
    label: "ElevenLabs Turbo v2.5",
    tier: "pro",
    hint: "fast",
    voices: [
      { id: "21m00Tcm4TlvDq8ikWAM", label: "Rachel (default)" },
      { id: "pNInz6obpgDQGcFmaJgB", label: "Adam" },
    ],
  },
  {
    id: "elevenlabs/eleven-v3",
    label: "ElevenLabs v3",
    tier: "pro",
    hint: "expressive",
    description: "Eleven v3 · audio tags · CF Unified Billing",
    voices: [
      { id: "21m00Tcm4TlvDq8ikWAM", label: "Rachel (default)" },
      { id: "pNInz6obpgDQGcFmaJgB", label: "Adam" },
      { id: "JBFqnCBsd6RMkjVDRZzb", label: "George" },
    ],
    costPer1kCharsUsd: 0.18,
  },
  {
    id: "google/gemini-3.1-flash-tts",
    label: "Gemini 3.1 Flash TTS",
    tier: "studio",
    hint: "Google · CF",
    description: "Gemini Flash TTS via Cloudflare Unified Billing",
    voices: [
      { id: "Puck", label: "Puck" },
      { id: "Zephyr", label: "Zephyr" },
      { id: "Charon", label: "Charon" },
      { id: "Kore", label: "Kore" },
      { id: "Fenrir", label: "Fenrir" },
      { id: "Aoede", label: "Aoede" },
    ],
    costPer1kCharsUsd: 0.02,
  },
  // MiniMax / Inworld are catalog placeholders only — omit until relay routes them.
];

export const DEFAULT_SUITE_MODEL_ID = "forge/suite-kokoro";
export const DEFAULT_SUITE_VOICE = "af_heart";

export function relayBase(): string {
  try {
    const url = (loadSuiteSettings().forgeApiUrl || "").trim().replace(/\/$/, "");
    if (url && !/localhost|127\.0\.0\.1/i.test(url)) return url;
  } catch {
    /* fall through */
  }
  return (
    (import.meta.env.VITE_RELAY_URL as string | undefined)?.replace(/\/$/, "") ||
    SUITE_RELAY_URL
  );
}

function mapCatalogEntry(raw: Record<string, unknown>): TtsModelOpt | null {
  const id = String(raw.id || raw.modelId || "").trim();
  if (!id) return null;
  // Hide placeholder engines that always 501 on /api/tts/synthesize
  if (!isSuiteSynthesizableModel(id)) return null;
  if (raw.routable === false) return null;
  const tierRaw = String(raw.tier || "budget").toLowerCase();
  const tier: TtsTier =
    tierRaw === "studio" || tierRaw === "pro" || tierRaw === "budget"
      ? tierRaw
      : /eleven|pro/.test(id)
        ? "pro"
        : /openai|xai|grok-tts|tts-1/.test(id)
          ? "studio"
          : "budget";
  const voicesRaw = Array.isArray(raw.voices) ? raw.voices : [];
  const voices: TtsVoiceOpt[] = voicesRaw
    .map((v) => {
      if (!v || typeof v !== "object") return null;
      const o = v as { id?: string; label?: string; hint?: string };
      if (!o.id) return null;
      return { id: String(o.id), label: String(o.label || o.id), hint: o.hint };
    })
    .filter(Boolean) as TtsVoiceOpt[];
  return {
    id,
    label: String(raw.label || raw.name || id.split("/").pop() || id),
    tier,
    hint: String(raw.listHint || raw.hint || ""),
    description: String(raw.description || ""),
    voices: voices.length ? voices : undefined,
    costPer1kCharsUsd:
      typeof raw.costPer1kCharsUsd === "number" ? raw.costPer1kCharsUsd : undefined,
    routable: raw.routable === false ? false : true,
  };
}

/** Load suite TTS models from BookForge relay (same engines as BookForge Gen chapter audio). */
export async function fetchSuiteTtsCatalog(
  token: string | null,
): Promise<{ models: TtsModelOpt[]; source: string; configured: boolean }> {
  // Only CORS-allowed headers (Authorization + Content-Type on POSTs).
  const headers: HeadersInit = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  // Prefer /api/tts/status (includes full catalog + voices)
  try {
    const res = await fetch(`${relayBase()}/api/tts/status`, {
      headers,
      signal: AbortSignal.timeout(20000),
    });
    if (res.ok) {
      const data = (await res.json()) as {
        configured?: boolean;
        catalog?: Record<string, unknown>[];
        preferredModels?: string[];
      };
      const models: TtsModelOpt[] = [];
      const seen = new Set<string>();
      for (const raw of data.catalog || []) {
        const m = mapCatalogEntry(raw);
        if (!m || seen.has(m.id)) continue;
        seen.add(m.id);
        models.push(m);
      }
      // Ensure static fallbacks present
      for (const f of FALLBACK_TTS_MODELS) {
        if (seen.has(f.id)) continue;
        seen.add(f.id);
        models.push(f);
      }
      if (models.length) {
        return {
          models,
          source: "tts/status",
          configured: data.configured !== false,
        };
      }
    }
  } catch {
    /* fall through */
  }

  // Fallback: /api/models/catalog?modality=tts
  try {
    const res = await fetch(`${relayBase()}/api/models/catalog?modality=tts`, {
      headers,
      signal: AbortSignal.timeout(25000),
    });
    if (res.ok) {
      const data = (await res.json()) as {
        curated?: Record<string, unknown>[];
        cloudflareTts?: Record<string, unknown>[];
        ok?: boolean;
      };
      const models: TtsModelOpt[] = [];
      const seen = new Set<string>();
      for (const raw of data.curated || []) {
        const m = mapCatalogEntry(raw);
        if (!m || seen.has(m.id)) continue;
        seen.add(m.id);
        models.push(m);
      }
      for (const raw of data.cloudflareTts || []) {
        const m = mapCatalogEntry(raw);
        if (!m || seen.has(m.id)) continue;
        // skip pure STT rows
        const blob = `${m.id} ${m.label}`.toLowerCase();
        if (/transcri|whisper|stt|asr|speech-to-text/.test(blob) && !/tts|aura|speech-2|eleven|kokoro|grok-tts/.test(blob)) {
          continue;
        }
        seen.add(m.id);
        models.push(m);
      }
      for (const f of FALLBACK_TTS_MODELS) {
        if (seen.has(f.id)) continue;
        seen.add(f.id);
        models.push(f);
      }
      if (models.length) {
        return { models, source: "models/catalog", configured: true };
      }
    }
  } catch {
    /* fall through */
  }

  return {
    models: FALLBACK_TTS_MODELS,
    source: "fallback",
    configured: false,
  };
}

export function voicesForModel(
  models: TtsModelOpt[],
  modelId: string,
): TtsVoiceOpt[] {
  const m = models.find((x) => x.id === modelId);
  if (m?.voices?.length) return m.voices;
  const fb = FALLBACK_TTS_MODELS.find((x) => x.id === modelId);
  if (fb?.voices?.length) return fb.voices;
  const id = modelId.toLowerCase();
  if (id.includes("xai") || id.includes("grok-tts")) {
    return FALLBACK_TTS_MODELS[0]!.voices!;
  }
  if (id.includes("openai") || id.includes("tts-1")) {
    return FALLBACK_TTS_MODELS[1]!.voices!;
  }
  if (id.includes("kokoro")) {
    return FALLBACK_TTS_MODELS.find((x) => x.id === "forge/suite-kokoro")?.voices
      || FALLBACK_TTS_MODELS[3]!.voices!;
  }
  if (id.includes("breeze")) {
    return [
      { id: "warm-narrator", label: "Warm narrator" },
      { id: "clear-woman", label: "Clear young woman" },
      { id: "restrained-man", label: "Restrained man" },
    ];
  }
  if (id.includes("aura")) {
    return FALLBACK_TTS_MODELS[6]!.voices!;
  }
  if (id.includes("eleven")) {
    return FALLBACK_TTS_MODELS.find((x) => x.id === "elevenlabs/eleven-multilingual-v2")?.voices
      || [{ id: "21m00Tcm4TlvDq8ikWAM", label: "Rachel (default)" }];
  }
  if (id.includes("gemini")) {
    return FALLBACK_TTS_MODELS.find((x) => x.id.includes("gemini"))?.voices
      || [{ id: "Puck", label: "Puck" }];
  }
  return [{ id: "default", label: "Default" }];
}

export function modelsByTier(models: TtsModelOpt[]): {
  studio: TtsModelOpt[];
  budget: TtsModelOpt[];
  pro: TtsModelOpt[];
} {
  return {
    studio: models.filter((m) => m.tier === "studio"),
    budget: models.filter((m) => m.tier === "budget"),
    pro: models.filter((m) => m.tier === "pro"),
  };
}

export function costHintForModel(
  models: TtsModelOpt[],
  modelId: string,
): number {
  const m = models.find((x) => x.id === modelId);
  if (m?.costPer1kCharsUsd != null) return m.costPer1kCharsUsd;
  const fb = FALLBACK_TTS_MODELS.find((x) => x.id === modelId);
  return fb?.costPer1kCharsUsd ?? 0.015;
}
